/**
 * Utility functions for sanitizing file names before upload
 */

// Configuration interface for file name sanitization
interface SanitizationConfig {
  replacement: string
  allowSpaces: boolean
  maxLength: number
  preserveCase: boolean
}

// Global configuration for file name sanitization
let SANITIZATION_CONFIG: SanitizationConfig = {
  replacement: '_',
  allowSpaces: false,
  maxLength: 255,
  preserveCase: true
}

/**
 * Sanitizes a filename by removing or replacing unsafe characters
 */
export function sanitizeFileName(fileName: string): string {
  if (!fileName || typeof fileName !== 'string') {
    return 'unnamed_file'
  }

  const {
    replacement,
    allowSpaces,
    maxLength,
    preserveCase
  } = SANITIZATION_CONFIG

  // Extract file extension
  const lastDotIndex = fileName.lastIndexOf('.')
  let name = fileName
  let extension = ''
  
  if (lastDotIndex > 0) {
    name = fileName.substring(0, lastDotIndex)
    extension = fileName.substring(lastDotIndex)
  }

  // Remove path traversal attempts and other dangerous patterns
  name = name.replace(/\.\./g, '') // Completely remove .. patterns
  name = name.replace(/[/\\]/g, replacement) // Remove path separators
  
  // Remove or replace unsafe characters
  // Keep alphanumeric, hyphens, underscores, and optionally spaces
  const safePattern = allowSpaces ? /[^a-zA-Z0-9\-_.() ]/g : /[^a-zA-Z0-9\-_.()]/g
  name = name.replace(safePattern, replacement)
  
  // If spaces are not allowed, replace them too
  if (!allowSpaces) {
    name = name.replace(/\s+/g, replacement)
  }
  
  // Remove leading/trailing dots and spaces (after other cleanups)
  name = name.replace(/^[.\s]+|[.\s]+$/g, '')
  
  // Replace multiple consecutive replacements with single replacement
  const multipleReplacements = new RegExp(`\\${replacement}+`, 'g')
  name = name.replace(multipleReplacements, replacement)
  
  // Remove leading/trailing replacement characters
  const leadingTrailingPattern = new RegExp(`^\\${replacement}+|\\${replacement}+$`, 'g')
  name = name.replace(leadingTrailingPattern, '')
  
  // Ensure name is not empty
  if (!name.trim()) {
    name = 'file'
  }

  // Handle case
  if (!preserveCase) {
    name = name.toLowerCase()
  }

  // Combine name and extension
  let sanitizedFileName = name + extension

  // Ensure total length doesn't exceed maximum
  if (sanitizedFileName.length > maxLength) {
    const availableLength = maxLength - extension.length
    if (availableLength > 0) {
      name = name.substring(0, availableLength)
      sanitizedFileName = name + extension
    } else {
      // If extension itself is too long, truncate it too
      sanitizedFileName = sanitizedFileName.substring(0, maxLength)
    }
  }

  return sanitizedFileName
}

/**
 * Sanitizes multiple file names
 */
export function sanitizeFileNames(files: File[]): File[] {
  if (!Array.isArray(files)) {
    return files
  }

  return files.map(file => {
    if (!(file instanceof File)) {
      return file
    }

    const sanitizedName = sanitizeFileName(file.name)
    
    // Create a new File object with the sanitized name if it changed
    if (sanitizedName !== file.name) {
      return new File([file], sanitizedName, {
        type: file.type,
        lastModified: file.lastModified
      })
    }
    
    return file
  })
}

/**
 * Updates the global sanitization configuration
 */
export function updateSanitizationConfig(config: Partial<SanitizationConfig>): void {
  SANITIZATION_CONFIG = { ...SANITIZATION_CONFIG, ...config }
}

/**
 * Gets the current sanitization configuration
 */
export function getSanitizationConfig(): SanitizationConfig {
  return { ...SANITIZATION_CONFIG }
}
